import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Clock, Tag } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { INSIGHT_CATEGORY_LABEL } from "@/content/insights/labels";
import type { InsightPost } from "@/content/insights/types";
import { getActiveProvince } from "@/provinces/_registry";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

interface InsightCard {
  slug: string;
  title: string;
  excerpt: string;
  category: InsightPost["category"];
  publishedAt: string;
  readingMinutes: number;
}

export const Route = createFileRoute("/insights/")({
  component: InsightsHub,
  loader: async (): Promise<{ hub: PageContent; posts: InsightCard[] }> => {
    const [{ HUB_INSIGHTS_CONTENT }, { INSIGHTS_META }] = await Promise.all([
      import("@/content/longform-hubs"),
      import("@/content/insights/meta"),
    ]);
    return {
      hub: HUB_INSIGHTS_CONTENT,
      posts: INSIGHTS_META.map((p) => ({
        slug: p.slug,
        title: p.title,
        excerpt: p.excerpt,
        category: p.category,
        publishedAt: p.publishedAt,
        readingMinutes: p.readingMinutes,
      })),
    };
  },
  head: ({ loaderData }) => {
    const INSIGHTS = loaderData?.posts ?? [];
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `บทความ & ไกด์ Notary / Visa / สมรส — Thai Notary ${config.nameTh}`;
    const description = `รวมบทความและไกด์อัปเดต 2026 สำหรับ Notary Public, MFA Apostille, จดทะเบียนสมรสไทย-ต่างชาติ, DTV Visa และงานแปลรับรอง โดยทีมทนายใน${config.nameTh}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/insights",
        alternates: [{ lang: "en", path: "/en/insights" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "บทความ", url: `${site}/insights` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${site}/insights#collection`,
            name: `Thai Notary Insights — ${config.nameTh}`,
            url: `${site}/insights`,
            inLanguage: "th-TH",
            isPartOf: { "@id": `${site}/#website` },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: INSIGHTS.length,
              itemListElement: INSIGHTS.map((p, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${site}/insights/${p.slug}`,
                name: p.title,
              })),
            },
          }),
        },
        ...(loaderData?.hub
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(buildFaqSchema(loaderData.hub.faqs)),
              },
            ]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/insights")) },
      ],
    };
  },
});

function InsightsHub() {
  const { config } = getActiveProvince();
  const { posts: INSIGHTS } = Route.useLoaderData() as { posts: InsightCard[] };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-14 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <BookOpen className="h-3.5 w-3.5" /> Insights & Guides
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              บทความและไกด์โดยทีมทนาย{config.nameTh}
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-base text-muted-foreground">
              เนื้อหาอัปเดตทุกเดือนจากเคสจริง — Notary Public, MFA/Apostille,
              จดทะเบียนสมรสไทย-ต่างชาติ, DTV Visa และงานแปลรับรอง
            </p>
          </div>
        </section>

        <section>
          <div className="mx-auto grid max-w-5xl gap-6 px-4 py-12 md:grid-cols-2">
            {INSIGHTS.map((p) => (
              <Link
                key={p.slug}
                to="/insights/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col rounded-xl border border-border bg-card p-6 transition hover:border-primary hover:shadow-lg"
              >
                <div className="flex items-center gap-2 text-xs font-semibold text-gold">
                  <Tag className="h-3 w-3" />
                  {INSIGHT_CATEGORY_LABEL[p.category]}
                </div>
                <h2 className="mt-2 text-lg font-bold leading-snug text-foreground group-hover:text-primary md:text-xl">
                  {p.title}
                </h2>
                <p className="mt-2 line-clamp-3 flex-1 text-sm text-muted-foreground">
                  {p.excerpt}
                </p>
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {new Date(p.publishedAt).toLocaleDateString("th-TH", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {p.readingMinutes} นาที
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
        <LongFormContent content={Route.useLoaderData().hub} />
      </main>
      <SiteFooter />
    </div>
  );
}